export const isVideoFile = (file) =>
  /\.(mp4|webm|mov|m4v|ogg|avi|mkv)(\?.*)?$/i.test(String(file || ""));

export const splitMedia = (files = []) => {
  const images = [];
  const videos = [];
  (Array.isArray(files) ? files : []).forEach((file) => {
    if (!file || typeof file !== "string") return;
    if (isVideoFile(file)) videos.push(file);
    else images.push(file);
  });
  return { images, videos };
};

export const firstImage = (item) => splitMedia(item?.files).images[0] || "";

export const asList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (value == null || value === "") return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const typeName = (item) => (item?.productType || "").trim() || "Other";

export const collectionName = (item) => (item?.collection || "").trim();

export const availableInventory = (item) =>
  (item?.inventory || []).filter(
    (row) =>
      !row?.isDeleted &&
      (row.stockStatus === "In Stock" || row.stockStatus === "Made-to-order"),
  );

export const stockLabel = (item) => {
  const rows = availableInventory(item);
  if (!rows.length) return "Out of stock";
  if (rows.some((row) => row.stockStatus === "In Stock")) return "In Stock";
  return "Made-to-order";
};

export const uniqueSorted = (values) =>
  [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b),
  );

export const matchesOccasion = (item, occasion) => {
  if (!occasion || occasion === "All") return true;
  return asList(item?.occasion).some(
    (value) => value.toLowerCase() === String(occasion).toLowerCase(),
  );
};

const roundMoney = (value) => Number(Math.max(Number(value) || 0, 0).toFixed(2));

export const priceBreakdown = (item, quantity = 1) => {
  const qty = Math.max(Number(quantity) || 1, 1);
  const mrpUnit = Number(item?.mrp) || 0;
  const discountPercent = Math.min(Math.max(Number(item?.discount) || 0, 0), 100);
  const discountedUnit = mrpUnit - (mrpUnit * discountPercent) / 100;
  const gstIncluded = Boolean(item?.gstIncluded);
  const gstPercent = gstIncluded
    ? Math.min(Math.max(Number(item?.gstPercentage) || 0, 0), 100)
    : 0;
  const taxUnit = (discountedUnit * gstPercent) / 100;

  return {
    mrp: roundMoney(mrpUnit * qty),
    discountPercent,
    discountAmount: roundMoney(((mrpUnit * discountPercent) / 100) * qty),
    discounted: roundMoney(discountedUnit * qty),
    unitDiscounted: roundMoney(discountedUnit),
    gstIncluded,
    gstPercent,
    tax: roundMoney(taxUnit * qty),
    payable: roundMoney((discountedUnit + taxUnit) * qty),
  };
};

export const payableAmount = (item, quantity) => {
  const stored = Number(item?.finalAmount);
  const qty = Math.max(Number(quantity) || 1, 1);
  if (Number.isFinite(stored) && stored >= 0) {
    return roundMoney(stored * qty);
  }
  return priceBreakdown(item, qty).payable;
};

export const formatMoney = (symbol, value) =>
  `${symbol}${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const inventoryLabel = (row, index, rows = []) => {
  const storeName = row?.store?.branchName || row?.store?.city || "";
  const parts = [row?.size, row?.color, row?.stockStatus, storeName].filter(Boolean);
  const key = parts.join("|");
  const duplicate =
    rows.filter((item) => {
      const otherStore = item?.store?.branchName || item?.store?.city || "";
      return (
        [item?.size, item?.color, item?.stockStatus, otherStore]
          .filter(Boolean)
          .join("|") === key
      );
    }).length > 1;
  if ((duplicate || !parts.length) && row?.tagNo) parts.push(row.tagNo);
  if (!parts.length) return `Option ${index + 1}`;
  return parts.join(" • ");
};

export const maxOrderQuantity = (row) => {
  const quantity = Number(row?.quantity);
  if (Number.isFinite(quantity) && quantity > 1) return Math.floor(quantity);
  return 10;
};

export const toCartItem = (item, quantity = 1) => {
  const row = availableInventory(item)[0] || null;
  const maxQty = maxOrderQuantity(row);
  const qty = Math.min(Math.max(Number(quantity) || 1, 1), maxQty);
  return {
    jewelleryId: String(item?._id || ""),
    inventoryId: row?._id ? String(row._id) : "",
    quantity: qty,
    maxQty,
    name: item?.name || "Artificial Jewellery",
    image: firstImage(item),
    caption: [(item?.subType || "").trim(), collectionName(item), ...asList(item?.occasion)]
      .filter(Boolean)
      .join(" • "),
    mrp: item?.mrp,
    discount: item?.discount,
    finalAmount: item?.finalAmount,
    gstIncluded: Boolean(item?.gstIncluded),
    gstPercentage: item?.gstPercentage,
    available: Boolean(row),
  };
};

export const linePayable = (item) => {
  const stored = Number(item?.amount);
  if (Number.isFinite(stored) && item?.amount != null && item?.amount !== "") {
    return roundMoney(stored);
  }
  return payableAmount(item, item?.quantity);
};

export const mapServerCartItems = (cart) => {
  const lines = Array.isArray(cart?.items) ? cart.items : [];
  return lines
    .map((line) => {
      const jewellery =
        line?.jewellery && typeof line.jewellery === "object" ? line.jewellery : {};
      const inventory =
        line?.inventory && typeof line.inventory === "object" ? line.inventory : {};
      const jewelleryId = jewellery._id || line?.jewellery;
      const inventoryId = inventory._id || line?.inventory;
      const quantity = Math.max(Number(line?.quantity) || 1, 1);
      const stock = Number(inventory.quantity);
      const maxQty = Number.isFinite(stock) ? Math.max(Math.floor(stock), quantity) : quantity;
      return {
        jewelleryId: String(jewelleryId || ""),
        inventoryId: String(inventoryId || ""),
        quantity,
        maxQty,
        amount: line?.amount,
        name: jewellery.name || "Artificial Jewellery",
        image: firstImage(jewellery),
        caption: [
          (jewellery.subType || "").trim(),
          collectionName(jewellery),
          ...asList(jewellery.occasion),
        ]
          .filter(Boolean)
          .join(" • "),
        mrp: jewellery.mrp,
        discount: jewellery.discount,
        finalAmount: jewellery.finalAmount,
        gstIncluded: Boolean(jewellery.gstIncluded),
        gstPercentage: jewellery.gstPercentage,
        available: Boolean(inventoryId),
      };
    })
    .filter((line) => line.jewelleryId && line.inventoryId);
};

const purchaseModeOn = (value, whenMissing) => {
  if (value == null || value === "") return whenMissing;
  if (value === true || String(value).toLowerCase() === "true") return true;
  if (value === false || String(value).toLowerCase() === "false") return false;
  return whenMissing;
};

export const jewelleryPurchaseModes = (appConfig) => {
  const purchase = appConfig?.jewelleryPurchase;
  return {
    online: purchaseModeOn(purchase?.online, true),
    offline: purchaseModeOn(purchase?.offline, false),
  };
};

export const cartTotals = (items = []) =>
  (Array.isArray(items) ? items : []).reduce(
    (totals, item) => {
      const price = priceBreakdown(item, item.quantity);
      totals.mrp = roundMoney(totals.mrp + price.mrp);
      totals.discountAmount = roundMoney(totals.discountAmount + price.discountAmount);
      totals.discounted = roundMoney(totals.discounted + price.discounted);
      totals.tax = roundMoney(totals.tax + price.tax);
      totals.payable = roundMoney(totals.payable + linePayable(item));
      totals.count += Number(item.quantity) || 0;
      return totals;
    },
    { mrp: 0, discountAmount: 0, discounted: 0, tax: 0, payable: 0, count: 0 },
  );
